import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Truck, Plus, Pencil, Trash2, Copy, KeyRound, Baby, Phone, Mail, Home, MapPin, ArrowUp, ArrowDown, Wand2, History, Radio, Hash, Clock, Gauge, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import DriverHistoryDialog from '@/components/DriverHistoryDialog';
import DriverLiveRouteDialog from '@/components/DriverLiveRouteDialog';
import API from '@/config/api';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  password: '',
  vehicle: '',
  license_plate: '',
  notes: '',
  assigned_children: [],
  transport_edits: {}, // child_id -> {pickup_address, pickup_label, dropoff_address, dropoff_label}
};

const DriversPage = () => {
  const { getAuthHeaders } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // driver being edited (or null for create)
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [credentialsDialog, setCredentialsDialog] = useState(null); // {email, password}
  const [historyDriver, setHistoryDriver] = useState(null);
  const [liveDriver, setLiveDriver] = useState(null);
  const [pinDriver, setPinDriver] = useState(null);
  const [pinValue, setPinValue] = useState('');
  const [pinSaving, setPinSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, cRes] = await Promise.all([
        axios.get(`${API}/drivers`, getAuthHeaders()),
        axios.get(`${API}/children`, getAuthHeaders()),
      ]);
      setDrivers(dRes.data || []);
      setChildren(cRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load drivers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (driver) => {
    setEditing(driver);
    // Prime transport edits from any child.transport values so admins see current values
    const transportEdits = {};
    (driver.assigned_children || []).forEach((cid) => {
      const c = children.find((ch) => ch.id === cid);
      if (c?.transport) {
        transportEdits[cid] = {
          pickup_address: c.transport.pickup_address || '',
          pickup_label: c.transport.pickup_label || '',
          pickup_time: c.transport.pickup_time || '',
          dropoff_address: c.transport.dropoff_address || '',
          dropoff_label: c.transport.dropoff_label || '',
          dropoff_time: c.transport.dropoff_time || '',
        };
      }
    });
    setForm({
      name: driver.name || '',
      email: driver.email || '',
      phone: driver.phone || '',
      password: '',
      vehicle: driver.vehicle || '',
      license_plate: driver.license_plate || '',
      notes: driver.notes || '',
      assigned_children: driver.assigned_children || [],
      transport_edits: transportEdits,
    });
    setDialogOpen(true);
  };

  const toggleChild = (childId) => {
    setForm((prev) => {
      const has = prev.assigned_children.includes(childId);
      return {
        ...prev,
        assigned_children: has
          ? prev.assigned_children.filter((id) => id !== childId)
          : [...prev.assigned_children, childId],
      };
    });
  };

  const moveChild = (index, delta) => {
    setForm((prev) => {
      const arr = [...prev.assigned_children];
      const newIdx = index + delta;
      if (newIdx < 0 || newIdx >= arr.length) return prev;
      [arr[index], arr[newIdx]] = [arr[newIdx], arr[index]];
      return { ...prev, assigned_children: arr };
    });
  };

  const [optimizing, setOptimizing] = useState(false);
  const [sortingByTime, setSortingByTime] = useState(false);

  const sortByTime = async () => {
    if (!editing) {
      toast.error('Save the driver first, then sort by time');
      return;
    }
    if (form.assigned_children.length < 2) return;
    setSortingByTime(true);
    try {
      await saveTransportEdits();
      const res = await axios.post(
        `${API}/drivers/${editing.id}/sort-by-time`,
        { direction: 'pickup' },
        getAuthHeaders(),
      );
      if (res.data.order) {
        setForm((prev) => ({ ...prev, assigned_children: res.data.order }));
        toast.success('Route sorted by scheduled pickup time');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not sort by time');
    } finally {
      setSortingByTime(false);
    }
  };

  const optimizeOrder = async () => {
    if (!editing) {
      toast.error('Save the driver first, then optimize');
      return;
    }
    if (form.assigned_children.length < 2) {
      toast.info('Add at least 2 children before optimizing');
      return;
    }
    setOptimizing(true);
    try {
      // Persist any pending transport edits first so geocoding uses fresh addresses
      await saveTransportEdits();
      const res = await axios.post(
        `${API}/drivers/${editing.id}/optimize-route`,
        { direction: 'pickup', apply: false },
        getAuthHeaders(),
      );
      if (res.data.order && res.data.order.length) {
        setForm((prev) => ({ ...prev, assigned_children: res.data.order }));
        const unplaced = res.data.unplaced || [];
        if (unplaced.length) {
          toast.warning(
            `Route optimized. ${unplaced.length} stop(s) had unrecognizable addresses and were placed at the end.`,
          );
        } else {
          toast.success('Route optimized — closest stops first from your center');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not optimize route');
    } finally {
      setOptimizing(false);
    }
  };

  const updateTransport = (childId, field, value) => {
    setForm((prev) => ({
      ...prev,
      transport_edits: {
        ...prev.transport_edits,
        [childId]: {
          ...(prev.transport_edits[childId] || {
            pickup_address: '',
            pickup_label: '',
            dropoff_address: '',
            dropoff_label: '',
          }),
          [field]: value,
        },
      },
    }));
  };

  const saveTransportEdits = async () => {
    // Save transport for each assigned child that has edits
    const entries = Object.entries(form.transport_edits).filter(([cid]) =>
      form.assigned_children.includes(cid),
    );
    await Promise.all(
      entries.map(([cid, t]) =>
        axios.put(`${API}/children/${cid}/transport`, t, getAuthHeaders()),
      ),
    );
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(
          `${API}/drivers/${editing.id}`,
          {
            name: form.name,
            phone: form.phone,
            vehicle: form.vehicle,
            license_plate: form.license_plate,
            notes: form.notes,
            assigned_children: form.assigned_children,
          },
          getAuthHeaders(),
        );
        await saveTransportEdits();
        toast.success('Driver updated');
        setDialogOpen(false);
        load();
      } else {
        const res = await axios.post(
          `${API}/drivers`,
          {
            name: form.name,
            email: form.email,
            phone: form.phone,
            password: form.password,
            vehicle: form.vehicle,
            license_plate: form.license_plate,
            notes: form.notes,
            assigned_children: form.assigned_children,
          },
          getAuthHeaders(),
        );
        await saveTransportEdits();
        setDialogOpen(false);
        if (res.data.temp_password) {
          setCredentialsDialog({ email: form.email, password: res.data.temp_password });
        } else {
          toast.success(`Driver ${form.name} created`);
        }
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save driver');
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (driver) => {
    try {
      const res = await axios.post(
        `${API}/drivers/${driver.id}/reset-password`,
        {},
        getAuthHeaders(),
      );
      setCredentialsDialog({ email: driver.email, password: res.data.temp_password });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    }
  };

  const openPinDialog = (driver) => {
    setPinDriver(driver);
    setPinValue('');
  };

  const savePin = async () => {
    if (!/^\d{4,6}$/.test(pinValue)) {
      toast.error('PIN must be 4 to 6 digits');
      return;
    }
    setPinSaving(true);
    try {
      await axios.post(
        `${API}/drivers/${pinDriver.id}/set-pin`,
        { pin: pinValue },
        getAuthHeaders(),
      );
      toast.success(`PIN set for ${pinDriver.name}. Share it and the /driver-pin URL with them.`);
      setPinDriver(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set PIN');
    } finally {
      setPinSaving(false);
    }
  };

  const clearPin = async () => {
    if (!pinDriver) return;
    try {
      await axios.delete(`${API}/drivers/${pinDriver.id}/pin`, getAuthHeaders());
      toast.success('PIN removed');
      setPinDriver(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove PIN');
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/drivers/${deleteTarget.id}`, getAuthHeaders());
      toast.success('Driver deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete driver');
    }
  };

  const copyCreds = () => {
    if (!credentialsDialog) return;
    const text = `Email: ${credentialsDialog.email}\nPassword: ${credentialsDialog.password}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Copy failed — please copy manually'),
    );
  };

  return (
    <DashboardLayout title="Drivers">
      <div className="space-y-6" data-testid="drivers-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Truck className="w-7 h-7 text-primary" /> Drivers
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Van/bus drivers who transport children to and from the center.
            </p>
          </div>
          <Button onClick={openCreate} className="btn-primary" data-testid="add-driver-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Driver
          </Button>
        </div>

        {/* Today's speed alerts */}
        <SpeedAlertsPanel getAuthHeaders={getAuthHeaders} />

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading drivers…</div>
        ) : drivers.length === 0 ? (
          <Card className="card-clay">
            <CardContent className="p-8 text-center space-y-2">
              <Truck className="w-10 h-10 text-muted-foreground mx-auto" />
              <div className="font-semibold">No drivers yet</div>
              <div className="text-sm text-muted-foreground">
                Add a driver and assign children to their van.
              </div>
              <Button onClick={openCreate} className="btn-primary mt-2" data-testid="empty-add-driver-btn">
                <Plus className="w-4 h-4 mr-1" /> Add your first driver
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {drivers.map((driver) => (
              <Card key={driver.id} className="card-clay min-w-0" data-testid={`driver-card-${driver.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <CardTitle className="text-lg flex items-center gap-2 min-w-0 flex-1">
                      <Truck className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="truncate">{driver.name}</span>
                    </CardTitle>
                    <div className="flex items-center gap-1 flex-wrap" data-testid={`driver-actions-${driver.id}`}>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setLiveDriver(driver)}
                        data-testid={`live-route-${driver.id}`}
                        aria-label="Live route"
                      >
                        <Radio className="w-4 h-4 text-emerald-600" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setHistoryDriver(driver)}
                        data-testid={`history-driver-${driver.id}`}
                        aria-label="Trip history"
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(driver)}
                        data-testid={`edit-driver-${driver.id}`}
                        aria-label="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openPinDialog(driver)}
                        data-testid={`set-pin-${driver.id}`}
                        aria-label="Set PIN"
                        title={driver.has_pin ? 'PIN is set — click to change' : 'Set a 4-digit PIN'}
                      >
                        <Hash className={`w-4 h-4 ${driver.has_pin ? 'text-emerald-600' : ''}`} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => resetPassword(driver)}
                        data-testid={`reset-password-${driver.id}`}
                        aria-label="Reset password"
                      >
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setDeleteTarget(driver)}
                        data-testid={`delete-driver-${driver.id}`}
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm pt-0">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" /> {driver.email}
                  </div>
                  {driver.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" /> {driver.phone}
                    </div>
                  )}
                  {driver.vehicle && (
                    <div className="text-xs text-muted-foreground">
                      Vehicle: <span className="text-foreground">{driver.vehicle}</span>
                      {driver.license_plate && <> · Plate: <span className="text-foreground">{driver.license_plate}</span></>}
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <Badge variant="secondary" className="flex items-center gap-1" data-testid={`driver-child-count-${driver.id}`}>
                      <Baby className="w-3.5 h-3.5" />
                      {driver.assigned_children?.length || 0} child(ren)
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="driver-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Driver' : 'Add Driver'}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update the driver's details and children assigned to their van."
                : 'Create a login for a van/bus driver and assign the children they transport.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Full name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Marcus Doyle"
                  data-testid="driver-form-name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="driver@example.com"
                  disabled={!!editing}
                  data-testid="driver-form-email"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 555-5555"
                  data-testid="driver-form-phone"
                />
              </div>
              {!editing && (
                <div>
                  <Label>Password (optional)</Label>
                  <Input
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Leave blank to auto-generate"
                    data-testid="driver-form-password"
                  />
                </div>
              )}
              <div>
                <Label>Vehicle</Label>
                <Input
                  value={form.vehicle}
                  onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
                  placeholder="e.g. 2019 Ford Transit"
                  data-testid="driver-form-vehicle"
                />
              </div>
              <div>
                <Label>License Plate</Label>
                <Input
                  value={form.license_plate}
                  onChange={(e) => setForm({ ...form, license_plate: e.target.value })}
                  placeholder="e.g. WI-ABC-1234"
                  data-testid="driver-form-plate"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Route notes, allergies to be aware of, backup contact…"
                data-testid="driver-form-notes"
              />
            </div>

            <div className="space-y-2">
              <Label>Children assigned to this van ({form.assigned_children.length})</Label>
              <p className="text-xs text-muted-foreground">
                Assign children to the van, order them by driving sequence, and set where the van picks them up and drops them off.
              </p>
              {children.length === 0 ? (
                <div className="text-sm text-muted-foreground">No children enrolled yet.</div>
              ) : (
                <div className="space-y-3" data-testid="driver-form-children">
                  {/* Assigned children in route order */}
                  {form.assigned_children.length > 0 && (
                    <div className="border border-border rounded-lg overflow-hidden">
                      <div className="bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary flex items-center justify-between gap-2">
                        <span>Route order ({form.assigned_children.length})</span>
                        {editing && form.assigned_children.length >= 2 && (
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={sortByTime}
                              disabled={sortingByTime || optimizing}
                              className="h-6 px-2 text-xs"
                              data-testid="driver-form-sort-time-btn"
                            >
                              <Clock className="w-3.5 h-3.5 mr-1" />
                              {sortingByTime ? 'Sorting…' : 'Sort by time'}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={optimizeOrder}
                              disabled={optimizing || sortingByTime}
                              className="h-6 px-2 text-xs"
                              data-testid="driver-form-optimize-btn"
                            >
                              <Wand2 className="w-3.5 h-3.5 mr-1" />
                              {optimizing ? 'Optimizing…' : 'Optimize by distance'}
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="max-h-96 overflow-y-auto divide-y divide-border/60">
                        {form.assigned_children.map((cid, idx) => {
                          const c = children.find((x) => x.id === cid);
                          if (!c) return null;
                          const t = form.transport_edits[cid] || c.transport || {};
                          return (
                            <div
                              key={cid}
                              className="p-2 text-sm bg-white"
                              data-testid={`driver-form-child-row-${cid}`}
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold"
                                  data-testid={`driver-form-stop-num-${cid}`}
                                >
                                  {idx + 1}
                                </span>
                                <Baby className="w-4 h-4 text-muted-foreground" />
                                <span className="flex-1 truncate">
                                  {`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.name || 'Child'}
                                </span>
                                {c.classroom && <Badge variant="secondary" className="text-xs">{c.classroom}</Badge>}
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => moveChild(idx, -1)}
                                  disabled={idx === 0}
                                  data-testid={`driver-form-move-up-${cid}`}
                                  aria-label="Move up"
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => moveChild(idx, 1)}
                                  disabled={idx === form.assigned_children.length - 1}
                                  data-testid={`driver-form-move-down-${cid}`}
                                  aria-label="Move down"
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-red-500"
                                  onClick={() => toggleChild(cid)}
                                  data-testid={`driver-form-remove-${cid}`}
                                  aria-label="Remove"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 pl-8 bg-slate-50/60 rounded p-2" data-testid={`driver-form-transport-${cid}`}>
                                <div>
                                  <Label className="flex items-center gap-1 text-xs">
                                    <Home className="w-3.5 h-3.5 text-primary" /> Pickup (AM → Center)
                                  </Label>
                                  <Input
                                    value={t.pickup_address || ''}
                                    onChange={(e) => updateTransport(cid, 'pickup_address', e.target.value)}
                                    placeholder="Address, e.g. 123 Oak St, Madison WI"
                                    className="h-8 text-xs"
                                    data-testid={`driver-transport-pickup-address-${cid}`}
                                  />
                                  <Input
                                    value={t.pickup_label || ''}
                                    onChange={(e) => updateTransport(cid, 'pickup_label', e.target.value)}
                                    placeholder="Label (e.g. Home, Lincoln Elementary)"
                                    className="h-8 text-xs mt-1"
                                    data-testid={`driver-transport-pickup-label-${cid}`}
                                  />
                                  <Input
                                    type="time"
                                    value={t.pickup_time || ''}
                                    onChange={(e) => updateTransport(cid, 'pickup_time', e.target.value)}
                                    className="h-8 text-xs mt-1"
                                    title="Scheduled pickup time"
                                    data-testid={`driver-transport-pickup-time-${cid}`}
                                  />
                                </div>
                                <div>
                                  <Label className="flex items-center gap-1 text-xs">
                                    <MapPin className="w-3.5 h-3.5 text-primary" /> Dropoff (PM → From Center)
                                  </Label>
                                  <Input
                                    value={t.dropoff_address || ''}
                                    onChange={(e) => updateTransport(cid, 'dropoff_address', e.target.value)}
                                    placeholder="Address"
                                    className="h-8 text-xs"
                                    data-testid={`driver-transport-dropoff-address-${cid}`}
                                  />
                                  <Input
                                    value={t.dropoff_label || ''}
                                    onChange={(e) => updateTransport(cid, 'dropoff_label', e.target.value)}
                                    placeholder="Label (e.g. Home, Aftercare)"
                                    className="h-8 text-xs mt-1"
                                    data-testid={`driver-transport-dropoff-label-${cid}`}
                                  />
                                  <Input
                                    type="time"
                                    value={t.dropoff_time || ''}
                                    onChange={(e) => updateTransport(cid, 'dropoff_time', e.target.value)}
                                    className="h-8 text-xs mt-1"
                                    title="Scheduled dropoff time"
                                    data-testid={`driver-transport-dropoff-time-${cid}`}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Available children to add */}
                  {(() => {
                    const unassigned = children.filter((c) => !form.assigned_children.includes(c.id));
                    if (unassigned.length === 0) return null;
                    return (
                      <div className="border border-border rounded-lg overflow-hidden">
                        <div className="bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground">
                          Available children ({unassigned.length})
                        </div>
                        <div className="max-h-56 overflow-y-auto divide-y divide-border/40">
                          {unassigned.map((c) => (
                            <button
                              type="button"
                              key={c.id}
                              onClick={() => toggleChild(c.id)}
                              className="flex items-center gap-2 p-2 w-full text-left text-sm hover:bg-muted transition-colors"
                              data-testid={`driver-form-add-child-${c.id}`}
                            >
                              <Plus className="w-4 h-4 text-primary" />
                              <Baby className="w-4 h-4 text-muted-foreground" />
                              <span className="flex-1">{`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.name || 'Child'}</span>
                              {c.classroom && <Badge variant="secondary" className="text-xs">{c.classroom}</Badge>}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="btn-primary" data-testid="driver-form-save">
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create driver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials popup */}
      <Dialog open={!!credentialsDialog} onOpenChange={(o) => !o && setCredentialsDialog(null)}>
        <DialogContent data-testid="driver-credentials-dialog">
          <DialogHeader>
            <DialogTitle>Driver credentials</DialogTitle>
            <DialogDescription>
              Share these with the driver. They&apos;ll be prompted to change the password on first login.
            </DialogDescription>
          </DialogHeader>
          {credentialsDialog && (
            <div className="space-y-2 bg-muted rounded-md p-3 font-mono text-sm">
              <div><span className="text-muted-foreground">Email:</span> {credentialsDialog.email}</div>
              <div><span className="text-muted-foreground">Password:</span> {credentialsDialog.password}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCredentialsDialog(null)}>Close</Button>
            <Button onClick={copyCreds} className="btn-primary" data-testid="copy-driver-credentials">
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-driver-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete driver?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteTarget?.name}</strong>&apos;s login and van manifest history.
              Children assignments will be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-driver">
              Delete driver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DriverHistoryDialog
        open={!!historyDriver}
        onClose={() => setHistoryDriver(null)}
        driver={historyDriver}
      />

      <DriverLiveRouteDialog
        open={!!liveDriver}
        onClose={() => setLiveDriver(null)}
        driver={liveDriver}
      />

      {/* Set/Change PIN dialog */}
      <Dialog open={!!pinDriver} onOpenChange={(o) => !o && setPinDriver(null)}>
        <DialogContent data-testid="driver-set-pin-dialog">
          <DialogHeader>
            <DialogTitle>Set PIN for {pinDriver?.name}</DialogTitle>
            <DialogDescription>
              A short 4-6 digit PIN lets this driver sign in fast from a shared tablet at{' '}
              <code className="text-primary">{typeof window !== 'undefined' ? window.location.origin : ''}/driver-pin</code>.
              Only they should know it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>New PIN</Label>
            <Input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
              placeholder="4-6 digits"
              autoFocus
              data-testid="driver-set-pin-input"
            />
            {pinDriver?.has_pin && (
              <p className="text-xs text-muted-foreground">
                This driver already has a PIN. Setting a new one replaces it.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            {pinDriver?.has_pin && (
              <Button
                variant="outline"
                onClick={clearPin}
                className="mr-auto"
                data-testid="driver-remove-pin"
              >
                Remove PIN
              </Button>
            )}
            <Button variant="outline" onClick={() => setPinDriver(null)} disabled={pinSaving}>
              Cancel
            </Button>
            <Button
              onClick={savePin}
              disabled={pinSaving || pinValue.length < 4}
              className="btn-primary"
              data-testid="driver-save-pin"
            >
              {pinSaving ? 'Saving…' : 'Save PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// ==================== TODAY'S SPEED ALERTS ====================

const SpeedAlertsPanel = ({ getAuthHeaders }) => {
  const [alerts, setAlerts] = useState([]);
  const [motion, setMotion] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [a, m] = await Promise.all([
        axios.get(`${API}/transport/speed-alerts?date=${today}`, getAuthHeaders()),
        axios.get(`${API}/transport/motion-events?date=${today}`, getAuthHeaders()),
      ]);
      setAlerts(a.data?.alerts || []);
      setMotion(m.data?.events || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalEvents = alerts.length + motion.length;
  if (loading && totalEvents === 0) return null;
  if (!totalEvents) {
    return (
      <Card className="card-clay border-green-200 bg-green-50/40" data-testid="speed-alerts-panel">
        <CardContent className="p-3 flex items-center gap-3">
          <Gauge className="w-5 h-5 text-green-600" />
          <div>
            <p className="text-sm font-semibold">No driving alerts today</p>
            <p className="text-xs text-muted-foreground">Speed + motion events are logged live from each driver's device.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const motionLabel = (t) => ({
    hard_brake: 'Hard brake',
    harsh_turn: 'Harsh turn',
    hard_accel: 'Hard accel',
    jolt: 'Jolt / bump',
  }[t] || t);

  return (
    <Card className="card-clay border-2 border-red-300 bg-red-50/40" data-testid="speed-alerts-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          Driving alerts today
          <span className="text-sm font-normal text-muted-foreground">
            ({alerts.length} speed{alerts.length !== 1 ? 's' : ''}, {motion.length} motion)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="max-h-72 overflow-y-auto space-y-2">
          {alerts.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-2 p-2 bg-white rounded-lg border border-red-200" data-testid={`speed-alert-${a.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  <span className="inline-block w-20 text-[10px] font-bold uppercase tracking-wide text-red-600 mr-1">Speeding</span>
                  {a.driver_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(a.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {' • '}
                  {a.latitude?.toFixed(4)}, {a.longitude?.toFixed(4)}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="text-lg font-bold text-red-600 tabular-nums">{a.speed_mph}</div>
                <div className="text-[10px] text-red-700">+{a.over_by_mph} over {a.speed_limit_mph}</div>
              </div>
            </div>
          ))}
          {motion.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-2 p-2 bg-white rounded-lg border border-orange-200" data-testid={`motion-event-${m.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  <span className="inline-block w-20 text-[10px] font-bold uppercase tracking-wide text-orange-600 mr-1">{motionLabel(m.event_type)}</span>
                  {m.driver_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {m.speed_mph != null && ` • ${m.speed_mph} mph`}
                  {m.latitude != null && ` • ${m.latitude.toFixed(4)}, ${m.longitude.toFixed(4)}`}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="text-lg font-bold text-orange-600 tabular-nums">{m.g_force}g</div>
                <div className="text-[10px] text-orange-700">peak force</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};


export default DriversPage;